import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTranslation } from 'react-i18next'
import { colors } from '@/styles/colors'
import { isAramcoEnv } from '@/config/environment'

// Constants
export interface Agent {
  id: string
  name: string
  role: string
  title: string
  image: string
  isOnline: boolean
  availableForMeeting: boolean
  /** FS-574: false when the tenant hasn't provisioned+enabled this agent.
   *  Unavailable agents stay visible (no online dot) but must never be
   *  treated as usable — consumers that act on agents filter on this. */
  isAvailable: boolean
}

interface AgentSelectorProps {
  agents: Agent[]
  selectedAgent: Agent
  onAgentSelect: (agent: Agent) => void
}

const AgentSelector = ({
  agents,
  selectedAgent,
  onAgentSelect,
}: AgentSelectorProps) => {
  // Pastel colors array for consistent color assignment by index
  const pastelColors = [
    colors.yellow[200], // 1st
    colors.blue[200], // 2nd
    colors.green[200], // 3rd
    colors.orange[200], // 4th
    colors.purple[200], // 5th
    colors.teal[200], // 6th
    colors.pink[200], // 7th
    colors.indigo[200], // 8th
  ]

  const { t } = useTranslation()

  return (
    <TooltipProvider>
      <div className="flex gap-4">
        {agents.map((agent, index) => (
          <Tooltip key={agent.id}>
            <TooltipTrigger asChild>
              <div
                className={`relative p-0 h-auto group hover:bg-transparent ${
                  !agent.isOnline
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer'
                }`}
                onClick={() => agent.isOnline && onAgentSelect(agent)}
              >
                <div className="relative">
                  <div
                    className="w-12 h-12 rounded-full shadow-md flex items-center justify-center overflow-hidden"
                    style={{
                      backgroundColor: pastelColors[index % pastelColors.length],
                      border: selectedAgent.id === agent.id ? `2px solid ${colors.orange[300]}` : 'none'
                    }}
                  >
                    <img
                      src={agent.image}
                      alt={agent.name}
                      className="w-12 h-12 object-cover rounded-full"
                    />
                  </div>
                  {/* Online status indicator */}
                  {agent.isOnline && (
                    <div className="absolute top-8 left-9 w-4 h-4 rounded-full border-2 border-white" style={{ backgroundColor: colors.success[500] }} />
                  )}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>
                {agent.isOnline || isAramcoEnv
                  ? agent.title
                  : `${agent.title} - ${t('general.coming_soon')}`}
              </p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  )
}

export default AgentSelector
